import React from 'react';
import includes from 'lodash/includes';

// utils
import { CSTriggerSourceFromHeaderTitlesMap, HeaderTitles } from '../../constants';

// styles
import { StyledNotificationCircle, StyledSectionLeft } from './style';
import { StyledMessageTab } from '../../style';

import HeaderNavigationClickstreamEvents from '../../models/HeaderNavigationClickstreamEvents';

// components
import HeaderButton from './components/HeaderButton';

// types
import type { Paths } from '@zg-rentals/paths-manifest';
import type { Displays } from '../../interfaces/Displays';
import type { User } from '../../interfaces/user';
import type { LinkProps } from '../../interfaces/linkProps';

interface Props {
  displays: Displays;
  escapeAllLinks: boolean;
  link?: React.ComponentType<React.PropsWithChildren<LinkProps>>;
  pathname: string;
  paths: Paths;
  showUnreadDot: boolean;
  unreadCountDisplay: string;
  useAbsolutePaths: boolean;
  userModel: User | null;
}

const SectionLeft = ({
  displays,
  escapeAllLinks,
  link,
  pathname,
  paths,
  showUnreadDot,
  unreadCountDisplay,
  useAbsolutePaths,
  userModel,
}: Props) => {
  const {
    analyticsPage,
    analyticsOverview,
    billingPage,
    billingDue,
    leadsPage,
    maintenanceTrackerPage,
    propertiesPage,
    legacyZRM,
  } = paths;

  const { loggedIn } = userModel ?? {};

  const {
    displayAnalytics,
    displayMainBilling,
    displayMainLeads,
    displayMainMaintenanceTracker,
    displayMainMessages,
    displayMainMyProperties,
  } = displays;

  const handleLinkClick = (pageClickingTo: string) => {
    if (loggedIn && userModel) {
      const csLink = CSTriggerSourceFromHeaderTitlesMap[pageClickingTo];

      if (csLink) {
        HeaderNavigationClickstreamEvents.ClickNavigationLink({ user: userModel, link: csLink });
      } else {
        // eslint-disable-next-line
        console.error('ERROR@handleLinkClick: trigger source does not exist.', csLink);
      }
    }
  };

  return (
    <StyledSectionLeft>
      {loggedIn && displayMainMyProperties && (
        <li
          className="SectionLeft-link"
          onClick={() => {
            handleLinkClick(HeaderTitles.myProperties);
          }}
        >
          <HeaderButton
            className={includes(pathname, paths.propertiesPage) ? 'Link Link-active' : 'Link'}
            escape={escapeAllLinks}
            link={link}
            path={propertiesPage}
            title={HeaderTitles.myProperties}
            useAbsolutePaths={useAbsolutePaths}
          >
            {HeaderTitles.myProperties}
          </HeaderButton>
        </li>
      )}

      {displayMainMessages && (
        <li className="SectionLeft-link" onClick={() => handleLinkClick(HeaderTitles.messages)}>
          <StyledMessageTab>
            <HeaderButton
              className={includes(pathname, 'inbox') ? 'Link Link-active Link-messages' : 'Link Link-messages'}
              escape={true}
              link={link}
              path={legacyZRM.inbox}
              title={`${HeaderTitles.messages}${unreadCountDisplay.length > 0 ? unreadCountDisplay : ''}`}
              useAbsolutePaths={useAbsolutePaths}
            >
              <span className="messages-title" title={HeaderTitles.messages}>
                {HeaderTitles.messages}
              </span>
              {unreadCountDisplay.length > 0 && (
                <span className="HeaderNav-msg-count" title={unreadCountDisplay}>
                  {unreadCountDisplay}
                </span>
              )}
              {showUnreadDot && <StyledNotificationCircle />}
            </HeaderButton>
          </StyledMessageTab>
        </li>
      )}

      {displayMainMaintenanceTracker && (
        <li className="SectionLeft-link" onClick={() => handleLinkClick(HeaderTitles.maintenanceTracker)}>
          <StyledMessageTab>
            <HeaderButton
              className={
                includes(pathname, 'rental-manager/maintenance-tasks')
                  ? 'Link Link-active Link-messages'
                  : 'Link Link-messages'
              }
              escape={true}
              link={link}
              path={maintenanceTrackerPage}
              title={HeaderTitles.maintenanceTracker}
              useAbsolutePaths={useAbsolutePaths}
            >
              {HeaderTitles.maintenanceTracker}
            </HeaderButton>
          </StyledMessageTab>
        </li>
      )}

      {displayMainLeads && (
        <li className="SectionLeft-link" onClick={() => handleLinkClick(HeaderTitles.leads)}>
          <HeaderButton
            className={includes(pathname, 'leads') ? 'Link Link-active' : 'Link'}
            escape={true}
            link={link}
            path={leadsPage}
            title={HeaderTitles.leads}
            useAbsolutePaths={useAbsolutePaths}
          >
            {HeaderTitles.leads}
          </HeaderButton>
        </li>
      )}

      {displayAnalytics && (
        <li className="SectionLeft-link" onClick={() => handleLinkClick(HeaderTitles.analytics)}>
          <HeaderButton
            className={includes(pathname, paths.analyticsPage) ? 'Link Link-active' : 'Link'}
            escape={true}
            path={`${analyticsPage}/${analyticsOverview}`}
            title={HeaderTitles.analytics}
            useAbsolutePaths={useAbsolutePaths}
          >
            {HeaderTitles.analytics}
          </HeaderButton>
        </li>
      )}

      {displayMainBilling && (
        <li className="SectionLeft-link" onClick={() => handleLinkClick(HeaderTitles.billing)}>
          <HeaderButton
            className={includes(pathname, billingPage) ? 'Link Link-active' : 'Link'}
            escape={true}
            path={`${billingPage}/${billingDue}`}
            title={HeaderTitles.billing}
            useAbsolutePaths={useAbsolutePaths}
          >
            {HeaderTitles.billing}
          </HeaderButton>
        </li>
      )}
    </StyledSectionLeft>
  );
};

export default SectionLeft;
